/**
 * DisplayTopStrip — Display mode only
 *
 * Student-facing top bar. Three zones:
 *   LEFT  — unit name / lesson date
 *   CENTER — current segment (NOW) + next segment
 *   RIGHT  — timer countdown (read-only, large) with progress bar
 *
 * No interactive elements. High-contrast on dark board backgrounds.
 * NEVER render this in Edit or Presenter mode.
 */
import React, { useState, useEffect, useRef } from 'react';

// Section type color strip helper (mirrors TCP getSectionTheme)
const getSectionAccent = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('bell') || t.includes('warm') || t.includes('ringer')) return '#f59e0b';
    if (t.includes('objective') || t.includes('goal')) return '#10b981';
    if (t.includes('direct') || t.includes('instruction')) return '#3b82f6';
    if (t.includes('guided') || t.includes('practice')) return '#8b5cf6';
    if (t.includes('independent') || t.includes('work time')) return '#06b6d4';
    if (t.includes('discussion') || t.includes('partner')) return '#f97316';
    if (t.includes('exit') || t.includes('closing') || t.includes('ticket')) return '#ec4899';
    return '#64748b';
};

const DisplayTopStrip = ({
    unitName = '',
    lessonDate = '',
    currentSegment = null,   // { title, duration }
    nextSegment = null,      // { title } — optional next segment
    timerSeconds = 0,        // timer seed from PinnedEssentialsBar (lifted through TCP)
    timerRunning = false,
    timerInitialSeconds = 0, // for progress bar ratio
}) => {
    // Internal countdown state — only used when timer is running.
    const [countdown, setCountdown] = useState(timerSeconds);
    const intervalRef = useRef(null);

    // Transition effect: start/stop timer based on timerRunning prop.
    const wasRunningRef = useRef(timerRunning);
    useEffect(() => {
        const wasRunning = wasRunningRef.current;
        wasRunningRef.current = timerRunning;

        if (timerRunning && !wasRunning) {
            setCountdown(timerSeconds);
            intervalRef.current = setInterval(() => {
                setCountdown(c => Math.max(0, c - 1));
            }, 1000);
        } else if (!timerRunning && wasRunning) {
            clearInterval(intervalRef.current);
        }

        return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timerRunning]);

    // Rendered seconds: live countdown when running, prop value when paused/stopped
    const displaySeconds = timerRunning ? countdown : timerSeconds;
    const initialSecs = timerInitialSeconds || timerSeconds || 1;
    const progressRatio = displaySeconds / initialSecs;

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    };

    const getTimerColor = () => {
        if (!timerRunning || timerSeconds === 0) return 'text-white/80';
        if (progressRatio > 0.5) return 'text-emerald-300';
        if (progressRatio > 0.25) return 'text-amber-300';
        return 'text-red-300';
    };

    const getProgressColor = () => {
        if (progressRatio > 0.5) return '#34d399'; // emerald
        if (progressRatio > 0.25) return '#fbbf24'; // amber
        return '#f87171'; // red
    };

    const leftLabel = unitName || lessonDate || 'Saige';
    const centerTitle = currentSegment?.title || '';
    const sectionAccent = getSectionAccent(centerTitle);

    return (
        <div
            className="w-full relative overflow-hidden"
            style={{
                height: '72px',
                background: 'rgba(0,0,0,0.40)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}
        >
            {/* Section type color strip — left edge */}
            {centerTitle && (
                <div
                    className="absolute left-0 top-0 bottom-0 w-1"
                    style={{ background: sectionAccent }}
                />
            )}

            <div className="flex items-center justify-between h-full px-8">
                {/* LEFT — Unit name / date */}
                <div className="flex-1 min-w-0">
                    <span
                        className="text-white/50 font-medium truncate block"
                        style={{ fontSize: 'clamp(0.75rem, 1.1vw, 0.9rem)' }}
                    >
                        {leftLabel}
                    </span>
                </div>

                {/* CENTER — Current segment + next */}
                <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-4">
                    {centerTitle ? (
                        <>
                            <span
                                className="text-white font-bold truncate text-center leading-tight"
                                style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}
                            >
                                {centerTitle}
                            </span>
                            {nextSegment?.title && (
                                <span
                                    className="text-white/40 text-center truncate mt-0.5"
                                    style={{ fontSize: 'clamp(0.65rem, 1vw, 0.8rem)' }}
                                >
                                    Up next: {nextSegment.title}
                                </span>
                            )}
                        </>
                    ) : null}
                </div>

                {/* RIGHT — Timer (read-only) */}
                <div className="flex-1 flex flex-col items-end justify-center min-w-0">
                    {timerSeconds > 0 && (
                        <>
                            <span
                                className={`font-bold tabular-nums transition-colors leading-none ${getTimerColor()}`}
                                style={{ fontSize: 'clamp(1.35rem, 2.5vw, 2rem)', fontFamily: 'ui-monospace, monospace' }}
                            >
                                {formatTime(displaySeconds)}
                            </span>
                            {/* Progress bar */}
                            {timerRunning && (
                                <div className="mt-1.5 w-20 h-1 rounded-full bg-white/15 overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{
                                            width: `${Math.max(0, progressRatio * 100)}%`,
                                            background: getProgressColor(),
                                            transition: 'width 1s linear, background 0.5s'
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DisplayTopStrip;
